import { v4 as uuidv4 } from 'uuid';

import { deDupeInputDisplayNameAndId } from '@/components/Nodes/FigmaCompositor/utils';
import type { NodeId, InputId, DataType } from 'web';
import type { FigmaCompositorData } from '@/types/node';
import type { ImageFile, EvaluatedValue } from '@/designer/designer';
import type { FlowGraph } from '../FlowGraph';

export class FigmaCompositorView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  getInputIds(): InputId[] {
    return this.graph.getInputIds(this.nodeId);
  }

  getInputs(): Record<InputId, EvaluatedValue | undefined> {
    return Object.fromEntries(
      this.graph.getInputIds(this.nodeId).map((id) => [id, this.graph.evaluateInput(this.nodeId, id)]),
    );
  }

  setOutputImage(outputImage: ImageFile): void {
    const data = this.graph.store.getNodeDataNullable<FigmaCompositorData>(this.nodeId);
    if (data?.outputImage?.url === outputImage.url) return;
    this.graph.wasmChange((wasm) => wasm.setFigmaOutputImage(this.nodeId, outputImage));
  }

  getFigmaNodeId(inputId: InputId): string | null {
    return this.graph.wasm.call((wasm) => wasm.getFigmaNodeId(this.nodeId, inputId)) ?? null;
  }

  setFigmaNodeId(inputId: InputId, figmaNodeId: string | null): void {
    if (this.getFigmaNodeId(inputId) === figmaNodeId) return;
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.setFigmaNodeId(this.nodeId, inputId, figmaNodeId ?? undefined)),
    );
  }

  getFigmaPaintId(inputId: InputId): string | null {
    return this.graph.wasm.call((wasm) => wasm.getFigmaPaintId(this.nodeId, inputId)) ?? null;
  }

  setFigmaPaintId(inputId: InputId, paintId: string | null): void {
    this.graph.edit(() =>
      this.graph.wasmChange((wasm) => wasm.setFigmaPaintId(this.nodeId, inputId, paintId ?? undefined)),
    );
  }

  addNodeInputHandle(inputName?: string, existingFigmaNodeId?: string, validInputTypes?: DataType[]) {
    this.graph.edit(() => {
      const existingInputIds = this.graph.getInputIds(this.nodeId);
      const order = existingInputIds.length;
      const baseName = inputName ?? `layer_${order + 1}`;
      const newInputDisplayNameAndId = deDupeInputDisplayNameAndId(baseName, existingInputIds);

      const undoEntry = this.graph.wasmChange((wasm) =>
        wasm.addInputHandle(this.nodeId, newInputDisplayNameAndId, validInputTypes),
      );

      undoEntry.add(
        this.graph.addNodeInputHandle(this.nodeId, newInputDisplayNameAndId, {
          description: '',
          label: newInputDisplayNameAndId,
          format: 'uri',
          id: uuidv4(),
          order,
          required: false,
        }),
      );

      if (existingFigmaNodeId) {
        undoEntry.add(
          this.graph.wasmChange((wasm) =>
            wasm.setFigmaNodeId(this.nodeId, newInputDisplayNameAndId, existingFigmaNodeId),
          ),
        );
      }

      return undoEntry;
    });
  }
}
